import { useEffect, useRef, type ReactNode } from 'react';
import { BRAND } from '@/brand/brand';

/**
 * The one modal every game state uses — game over, paused, settings, level complete, store.
 *
 * It fills the canvas rather than floating a small dialog over it, because on a phone a game's
 * interruption IS the whole screen: a small centred box surrounded by live gameplay reads as a web
 * page, not a game. Everything about the modal comes from the brand, so restyling all of them is one
 * edit here plus `brand.ts`.
 *
 * Accessibility is built in rather than bolted on: it announces itself as a dialog, moves focus in
 * on open, restores focus on close, and honours Escape when the state is dismissible.
 */
export interface GameModalProps {
	open: boolean;
	/** Small uppercase line above the headline — the state's name. */
	eyebrow?: string;
	/** The one big thing: a score, a title. */
	headline?: ReactNode;
	/** A quiet line under the headline. */
	sub?: ReactNode;
	children?: ReactNode;
	/** Provide to allow dismissal via Escape or a backdrop tap. Omit for states that must be resolved. */
	onDismiss?: () => void;
	/** How heavy the veil over the game is. 'full' for terminal states, 'veil' for pauses. */
	weight?: 'full' | 'veil';
}

export function GameModal({
	open,
	eyebrow,
	headline,
	sub,
	children,
	onDismiss,
	weight = 'veil',
}: GameModalProps) {
	const panelRef = useRef<HTMLDivElement>(null);
	const restoreFocusTo = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (!open) return;
		restoreFocusTo.current = document.activeElement as HTMLElement | null;
		// Focus the first control so a keyboard or switch user is not stranded behind the game.
		const first = panelRef.current?.querySelector<HTMLElement>('button, [href], input, select, textarea');
		first?.focus();

		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && onDismiss) {
				e.preventDefault();
				onDismiss();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => {
			window.removeEventListener('keydown', onKey);
			restoreFocusTo.current?.focus?.();
		};
	}, [open, onDismiss]);

	if (!open) return null;

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label={eyebrow ?? 'Game'}
			onPointerDown={(e) => {
				if (onDismiss && e.target === e.currentTarget) onDismiss();
			}}
			className="absolute inset-0 z-50 flex items-center justify-center px-6"
			style={{
				background: weight === 'full' ? `${BRAND.color.base}e0` : `${BRAND.color.base}99`,
				backdropFilter: 'blur(6px)',
				WebkitBackdropFilter: 'blur(6px)',
				color: BRAND.color.onBase,
				fontFamily: BRAND.type.body,
				paddingTop: 'env(safe-area-inset-top)',
				paddingBottom: 'env(safe-area-inset-bottom)',
				animation: 'gameModalIn 180ms ease-out',
			}}
		>
			<div ref={panelRef} className="flex w-full max-w-sm flex-col items-center gap-3 text-center">
				{eyebrow && (
					<p className="text-[11px] font-medium uppercase tracking-[0.3em] opacity-55">{eyebrow}</p>
				)}
				{headline !== undefined && (
					<div
						className="text-7xl font-semibold leading-none tabular-nums"
						style={{ fontFamily: BRAND.type.display }}
					>
						{headline}
					</div>
				)}
				{sub && <div className="text-sm opacity-75">{sub}</div>}
				{children && <div className="mt-3 flex w-full flex-col items-center gap-3">{children}</div>}
			</div>

			<style>{`
				@keyframes gameModalIn {
					from { opacity: 0; }
					to   { opacity: 1; }
				}
				@media (prefers-reduced-motion: reduce) {
					@keyframes gameModalIn { from, to { opacity: 1; } }
				}
			`}</style>
		</div>
	);
}

/** The modal's primary action. Brand-styled, thumb-sized, and the same in every state. */
export function GameModalButton({
	children,
	onClick,
	variant = 'primary',
}: {
	children: ReactNode;
	onClick: () => void;
	variant?: 'primary' | 'quiet';
}) {
	const primary = variant === 'primary';
	return (
		<button
			onClick={onClick}
			className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 px-8 font-medium transition-transform active:scale-[0.98]"
			style={{
				borderRadius: BRAND.radius,
				background: primary ? BRAND.color.primary : 'transparent',
				color: primary ? BRAND.color.base : BRAND.color.onBase,
				border: primary ? 'none' : `1px solid ${BRAND.color.onBase}33`,
			}}
		>
			{children}
		</button>
	);
}
